import { useCallback, useEffect, useState } from "react";
import { adjustStockInventory, fetchProductMovements, fetchStockBalances, purgeStockHistory } from "@/lib/adminStockApi";
import { hasPermission, type StockBalance, type StockMovement } from "@/lib/types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ClipboardEdit, Loader2, PackageOpen, Search, Trash2, Warehouse } from "lucide-react";
import { getApiErrorMessage } from "@/lib/api";
import { useApp } from "@/store/useApp";

function formatQty(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(3).replace(/\.?0+$/, "");
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const MOVEMENT_LABELS: Record<StockMovement["movementType"], string> = {
  RECEIVING: "Recebimento",
  PRODUCTION_CONSUMED: "Produção",
  INVENTORY_ADJUSTMENT: "Inventário",
};

export default function AdminStock() {
  const { account } = useApp();
  const canAdjust = hasPermission(account, "SUPRIMENTOS.ESTOQUE.AJUSTAR");
  const isAdmin = hasPermission(account, "SUPRIMENTOS.ESTOQUE.LIMPAR_HISTORICO");
  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Limpeza total do histórico (somente ADMIN)
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  // Movement history modal
  const [selectedBalance, setSelectedBalance] = useState<StockBalance | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [adjustQty, setAdjustQty] = useState<string>("");
  const [adjustNotes, setAdjustNotes] = useState<string>("");
  const [adjusting, setAdjusting] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchStockBalances();
      setBalances(data.sort((a, b) => a.productName.localeCompare(b.productName)));
    } catch {
      toast.error("Erro ao carregar estoque");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openMovements = async (b: StockBalance) => {
    setSelectedBalance(b);
    setAdjustQty(String(b.balance));
    setAdjustNotes("");
    setLoadingMovements(true);
    try {
      const data = await fetchProductMovements(b.productId);
      setMovements(data.filter(m => m.unit === b.unit));
    } catch {
      toast.error("Erro ao carregar movimentos");
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleAdjust = async () => {
    if (!selectedBalance) return;
    const parsed = Number(adjustQty.replace(",", "."));
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Quantidade inválida");
      return;
    }
    if (parsed === selectedBalance.balance) {
      toast.error("Quantidade igual ao saldo atual");
      return;
    }
    setAdjusting(true);
    try {
      await adjustStockInventory({
        productId: selectedBalance.productId,
        unit: selectedBalance.unit,
        newQuantity: parsed,
        notes: adjustNotes.trim() || undefined,
      });
      toast.success("Saldo ajustado");
      await load();
      // reload movements
      const data = await fetchProductMovements(selectedBalance.productId);
      const filtered = data.filter(m => m.unit === selectedBalance.unit);
      setMovements(filtered);
      // update local selectedBalance balance
      setSelectedBalance({ ...selectedBalance, balance: parsed });
      setAdjustQty(String(parsed));
      setAdjustNotes("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao ajustar saldo"));
    } finally {
      setAdjusting(false);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const result = await purgeStockHistory();
      toast.success(
        `Histórico zerado: ${result.deletedMovements} movimento(s), ${result.deletedReceivings} recebimento(s) e ${result.deletedFiles} arquivo(s) removidos`,
      );
      setPurgeOpen(false);
      setSelectedBalance(null);
      setMovements([]);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao limpar o histórico de estoque"));
    } finally {
      setPurging(false);
    }
  };

  const filtered = balances.filter(b => {
    const q = search.toLowerCase();
    return b.productName.toLowerCase().includes(q) || b.unit.toLowerCase().includes(q);
  });

  // Stats
  const totalProducts = new Set(balances.map(b => b.productId)).size;
  const negativeCount = balances.filter(b => b.balance <= 0).length;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Estoque</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Saldo de produtos in natura. Entradas via recebimentos, saídas via produção.
            </p>
          </div>
          <div className="flex gap-4 text-center">
            <div className="rounded-xl bg-white/10 px-4 py-2">
              <div className="text-2xl font-bold">{totalProducts}</div>
              <div className="text-xs text-primary-foreground/70">produtos</div>
            </div>
            {negativeCount > 0 && (
              <div className="rounded-xl bg-red-500/30 px-4 py-2">
                <div className="text-2xl font-bold text-red-200">{negativeCount}</div>
                <div className="text-xs text-red-200/80">negativos</div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
          <Search className="h-4 w-4 text-primary-foreground/60" />
          <input
            className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
            placeholder="Buscar por produto ou unidade"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Admin: limpar histórico */}
      {isAdmin && (
        <div className="flex flex-col gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-destructive">Zona de risco</div>
            <p className="text-xs text-muted-foreground">
              Apaga todos os movimentos, recebimentos e seus arquivos (comprovantes e assinaturas),
              zerando os saldos. Use para recomeçar do zero. Ação irreversível.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            className="shrink-0"
            onClick={() => setPurgeOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Zerar histórico de estoque
          </Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Warehouse className="h-10 w-10 opacity-30" />
          <p className="text-sm">{search ? "Nenhum produto encontrado" : "Nenhum movimento de estoque registrado"}</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto</TableHead>
                <TableHead>Unidade</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(b => (
                <TableRow
                  key={`${b.productId}-${b.unit}`}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => void openMovements(b)}
                >
                  <TableCell>
                    <div className="font-medium">{b.productName}</div>
                    <div className="text-xs text-muted-foreground">{b.categoryCode}</div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{b.unit}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-semibold tabular-nums ${b.balance <= 0 ? "text-red-600" : "text-green-700"}`}>
                      {b.balance <= 0 && "⚠ "}
                      {formatQty(b.balance)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Movement History Modal */}
      <Dialog open={!!selectedBalance} onOpenChange={open => { if (!open) { setSelectedBalance(null); setMovements([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageOpen className="h-5 w-5" />
              {selectedBalance?.productName} — {selectedBalance?.unit}
            </DialogTitle>
          </DialogHeader>

          {/* Current balance */}
          {selectedBalance && (
            <div className={`rounded-lg px-4 py-3 text-center font-semibold ${
              selectedBalance.balance <= 0
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-green-50 text-green-800 border border-green-200"
            }`}>
              Saldo atual: {formatQty(selectedBalance.balance)} {selectedBalance.unit}
              {selectedBalance.balance <= 0 && " ⚠"}
            </div>
          )}

          {canAdjust && selectedBalance && (
            <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ClipboardEdit className="h-4 w-4" /> Ajuste de inventário
              </div>
              <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
                <div className="space-y-1">
                  <Label className="text-xs">Nova quantidade</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={adjustQty}
                    onChange={(e) => setAdjustQty(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Motivo / observações</Label>
                  <Input
                    value={adjustNotes}
                    onChange={(e) => setAdjustNotes(e.target.value)}
                    placeholder="Ex.: Inventário semanal"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={() => void handleAdjust()} disabled={adjusting} size="sm">
                    {adjusting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Atualizar saldo
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                O ajuste registra a diferença com seu usuário ({account?.email}) e a data/hora atual.
              </p>
            </div>
          )}

          {loadingMovements ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : movements.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhum movimento registrado</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {m.quantity > 0
                            ? <ArrowUp className="h-3.5 w-3.5 text-green-600" />
                            : <ArrowDown className="h-3.5 w-3.5 text-red-500" />
                          }
                          <Badge variant={m.quantity > 0 ? "default" : "secondary"} className="text-xs">
                            {MOVEMENT_LABELS[m.movementType]}
                          </Badge>
                        </div>
                        {m.notes && <div className="mt-0.5 text-xs text-muted-foreground">{m.notes}</div>}
                      </TableCell>
                      <TableCell className={`text-right font-semibold tabular-nums ${m.quantity > 0 ? "text-green-700" : "text-red-600"}`}>
                        {m.quantity > 0 ? "+" : ""}{formatQty(m.quantity)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(m.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmação: zerar histórico de estoque */}
      <AlertDialog open={purgeOpen} onOpenChange={open => { if (!purging) setPurgeOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zerar todo o histórico de estoque?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso apaga <strong>todos os movimentos</strong> (recebimentos, consumo de produção e
              ajustes), <strong>todos os recebimentos registrados</strong> e os{" "}
              <strong>arquivos de comprovante e assinatura no storage</strong>, fazendo todos os
              saldos voltarem a zero. Esta ação é <strong>irreversível</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handlePurge(); }}
              disabled={purging}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purging && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sim, zerar tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
